
class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.quantityInput = null;
    this.decreaseBtn = null;
    this.increaseBtn = null;
    this.cartQuantityInput = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get elements
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.decreaseBtn = this.querySelector('[data-action="decrease"]');
    this.increaseBtn = this.querySelector('[data-action="increase"]');
    
    // Find cart quantity input
    this.cartQuantityInput = document.querySelector('[data-cart-quantity]');

    if (!this.quantityInput || !this.decreaseBtn || !this.increaseBtn) {
      console.warn('QuantitySelector: Required elements not found');
      return;
    }

    this.setInitialValues();
    
    this.bindEvents();
  }

  setInitialValues() {
    const initialQuantity = this.getAttribute('initial-quantity') || '1';
    const minQuantity = this.getAttribute('min-quantity') || '1';
    const maxQuantity = this.getAttribute('max-quantity') || '999';

    this.quantityInput.value = initialQuantity;
    this.quantityInput.setAttribute('min', minQuantity);
    this.quantityInput.setAttribute('max', maxQuantity);
  }

  bindEvents() {
    this.decreaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.decreaseQuantity();
    });

    this.increaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.increaseQuantity();
    });

    // Input validation
    this.quantityInput.addEventListener('input', () => {
      this.validateInput();
    });

    this.quantityInput.addEventListener('blur', () => {
      this.handleBlur();
    });

    this.quantityInput.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });
  }

  decreaseQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const minValue = parseInt(this.quantityInput.getAttribute('min')) || 1;
    
    if (currentValue > minValue) {
      this.updateQuantity(currentValue - 1);
    }
  }

  increaseQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const maxValue = parseInt(this.quantityInput.getAttribute('max')) || 999;
    
    if (currentValue < maxValue) {
      this.updateQuantity(currentValue + 1);
    }
  }

  updateQuantity(newValue) {
    this.quantityInput.value = newValue;
    
    if (this.cartQuantityInput) {
      this.cartQuantityInput.value = newValue;
    }
    
    this.dispatchQuantityEvents(newValue);
    
    this.dispatchEvent(new CustomEvent('quantity-changed', {
      detail: { 
        quantity: newValue,
        sectionId: this.getAttribute('section-id')
      },
      bubbles: true
    }));
  }

  dispatchQuantityEvents(quantity) {
    const events = ['input', 'change', 'keyup'];
    
    events.forEach(eventType => {
      const event = new Event(eventType, { bubbles: true, cancelable: true });
      this.quantityInput.dispatchEvent(event);
    });

    const customEvent = new CustomEvent('quantityUpdated', { 
      detail: { quantity: quantity },
      bubbles: true 
    });
    this.quantityInput.dispatchEvent(customEvent);

    const form = this.quantityInput.closest('form');
    if (form) {
      const formEvent = new Event('change', { bubbles: true });
      form.dispatchEvent(formEvent);
    }

    if (this.cartQuantityInput) {
      const cartForm = this.cartQuantityInput.closest('form');
      if (cartForm) {
        const cartFormEvent = new Event('change', { bubbles: true });
        cartForm.dispatchEvent(cartFormEvent);
      }
    }
  }

  validateInput() {
    const value = parseInt(this.quantityInput.value);
    const minValue = parseInt(this.quantityInput.getAttribute('min')) || 1;
    const maxValue = parseInt(this.quantityInput.getAttribute('max')) || 999;

    if (isNaN(value) || value < minValue) {
      this.quantityInput.value = minValue;
    } else if (value > maxValue) {
      this.quantityInput.value = maxValue;
    }
  }

  handleBlur() {
    if (this.quantityInput.value === '' || parseInt(this.quantityInput.value) < 1) {
      this.updateQuantity(1);
    } else {
      // Trigger update to sync with cart
      this.updateQuantity(parseInt(this.quantityInput.value));
    }
  }

  handleKeydown(e) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.increaseQuantity();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.decreaseQuantity();
    }
  }

  getQuantity() {
    return parseInt(this.quantityInput.value) || 1;
  }

  setQuantity(quantity) {
    const numQuantity = parseInt(quantity);
    if (!isNaN(numQuantity)) {
      this.updateQuantity(numQuantity);
    }
  }

  static get observedAttributes() {
    return ['initial-quantity', 'min-quantity', 'max-quantity'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.quantityInput) return;

    switch (name) {
      case 'initial-quantity':
        this.quantityInput.value = newValue || '1';
        break;
      case 'min-quantity':
        this.quantityInput.setAttribute('min', newValue || '1');
        break;
      case 'max-quantity':
        this.quantityInput.setAttribute('max', newValue || '999');
        break;
    }
  }
}


if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}
 